import { useRef, type ReactNode, type ButtonHTMLAttributes, type KeyboardEvent } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { ChatMessage } from '@multimod/shared';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { SubjectCard } from './SubjectCard';

export type IconTone = 'destructive' | 'warning';

const TONE_BG: Record<IconTone, string> = {
  destructive: 'bg-destructive/15 text-destructive border-destructive/30',
  warning: 'bg-warning/15 text-warning border-warning/30',
};

export const PLATFORM_NAME: Record<ChatMessage['platform'], string> = {
  twitch: 'Twitch',
  youtube: 'YouTube',
};

interface ShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon: ReactNode;
  iconTone?: IconTone;
  title: ReactNode;
  description?: ReactNode;
  msg?: ChatMessage;
  children?: ReactNode;
  /** Handler extra de keydown no content do dialog (numéricos, etc.). */
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  /**
   * CSS selector pro elemento que recebe foco ao abrir o dialog. Por padrão
   * Radix foca o Cancel (seguro pra destructive). Quando o dialog tem chips
   * ou presets como ação principal, passar o seletor faz arrows funcionarem
   * direto sem precisar Tab.
   */
  initialFocusSelector?: string;
}

/**
 * Handler genérico de arrow nav pra grupos de botões dentro de um dialog.
 * Move foco linearmente (Right/Down = próximo, Left/Up = anterior, com wrap).
 * Bind em <div onKeyDown={arrowNavHandler}> que contém os <button>s.
 */
export function arrowNavHandler(e: KeyboardEvent<HTMLDivElement>) {
  if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
  const buttons = Array.from(
    e.currentTarget.querySelectorAll<HTMLButtonElement>('button:not([disabled])'),
  );
  if (buttons.length === 0) return;
  e.preventDefault();
  const idx = buttons.findIndex((b) => b === document.activeElement);
  const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown';
  const next =
    idx === -1
      ? 0
      : forward
        ? (idx + 1) % buttons.length
        : (idx - 1 + buttons.length) % buttons.length;
  buttons[next]?.focus();
}

export function DialogShell({
  open,
  onOpenChange,
  icon,
  iconTone = 'destructive',
  title,
  description,
  msg,
  children,
  onKeyDown,
  initialFocusSelector,
}: ShellProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        ref={contentRef}
        className="max-w-lg"
        onOpenAutoFocus={(e) => {
          // Default Radix: foca o Cancel (proteção pra destructive). Quando o
          // dialog define initialFocusSelector, sobrescrevemos pra focar
          // chip/preset diretamente — assim arrows funcionam sem Tab.
          // queueMicrotask espera o focus-scope montar antes de chamar focus.
          if (initialFocusSelector) {
            e.preventDefault();
            queueMicrotask(() => {
              contentRef.current
                ?.querySelector<HTMLElement>(initialFocusSelector)
                ?.focus();
            });
          }
        }}
        onKeyDown={(e) => {
          // ⌘⏎ / Ctrl⏎ dispara o action button (qualquer DialogDestructive
          // dentro do shell carrega data-action="confirm"). Clicar
          // programaticamente garante que Radix feche o dialog corretamente.
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            const btn = contentRef.current?.querySelector<HTMLButtonElement>(
              'button[data-action="confirm"]:not([disabled])',
            );
            if (btn) {
              e.preventDefault();
              btn.click();
              return;
            }
          }
          onKeyDown?.(e);
        }}
        onCloseAutoFocus={(e) => {
          // #58: por padrão Radix joga foco no trigger; em moderation o trigger
          // é o feed (role=log, tabIndex=0). Sem isso o foco cai no <body> e
          // hotkeys B/T/D (registradas no role=log via onKeyDown) ficam mortas
          // até clique manual.
          //
          // Diferido em microtask pra esperar o focus-scope do Radix liberar
          // o aria-hidden do feed — sem isso o .focus() é silenciosamente
          // ignorado e o foco escorrega pro body.
          //
          // TODO(multi-panel): selector global pega o primeiro [role=log] do
          // DOM. Quando o painel virar multi-live lado a lado, precisa receber
          // o feed-ref via prop ou aria-controls do trigger. Não bloqueia hoje
          // (single-panel MVP).
          const feed = document.querySelector<HTMLElement>('[role="log"]');
          if (feed) {
            e.preventDefault();
            queueMicrotask(() => feed.focus());
          }
        }}
      >
        <div className="flex items-start gap-3">
          {icon ? (
            <div
              className={
                'w-10 h-10 rounded-md grid place-items-center border shrink-0 ' + TONE_BG[iconTone]
              }
            >
              {icon}
            </div>
          ) : null}
          <div className="flex-1 min-w-0">
            <AlertDialogTitle className="text-[17px] font-semibold tracking-tight m-0">
              {title}
            </AlertDialogTitle>
            {description ? (
              <AlertDialogDescription className="mt-1.5 text-sm text-fg-muted leading-relaxed">
                {description}
              </AlertDialogDescription>
            ) : null}
          </div>
        </div>
        {msg ? <SubjectCard msg={msg} /> : null}
        {children}
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface FooterProps {
  children: ReactNode;
  hint?: ReactNode;
}

export function DialogFooter({ children, hint }: FooterProps) {
  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      {hint ? (
        <span className="text-[11px] text-fg-subtle mr-auto whitespace-nowrap">{hint}</span>
      ) : null}
      <div className={'flex justify-end gap-2 flex-wrap ' + (hint ? '' : 'w-full')}>
        {children}
      </div>
    </div>
  );
}

export function DialogCancel({ children = 'Cancelar' }: { children?: ReactNode }) {
  return (
    <AlertDialogCancel asChild>
      <button
        type="button"
        className="h-11 min-w-[44px] px-4 rounded-md text-fg-muted hover:text-fg hover:bg-bg-hover"
      >
        {children}
      </button>
    </AlertDialogCancel>
  );
}

type DestructiveProps = ButtonHTMLAttributes<HTMLButtonElement>;

export function DialogDestructive({ children, className = '', ...rest }: DestructiveProps) {
  return (
    <AlertDialogAction asChild>
      <button
        type="button"
        data-action="confirm"
        {...rest}
        className={
          'h-11 min-w-[44px] px-4 rounded-md bg-destructive text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed ' +
          className
        }
      >
        {children}
      </button>
    </AlertDialogAction>
  );
}

export function ReversibilityNote({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 p-2.5 rounded-md border border-warning/30 bg-warning/10 text-xs text-warning-fg leading-relaxed">
      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-warning" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}
